import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { ElementChangedFlags, NodeUserData, PbrMaterial, SceneElement } from "../core/mdl";
import { deserializeNode, serializeNode } from "../core/mdl/io";
import { getRotationQuaternion, quaternionToAxisAngle } from "../core/utils";
import * as basket from "../eaiws/basket";
import { CalculationSheetInfo } from "../eaiws/basket";
import { ListenableEvent, Log } from "../utils";
import { ajax, Lock } from "../utils/async";
import { hash } from "../utils/arraybuffer";
import { contains } from "../utils/array";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError, rejectError } from "../utils/error";
import { getFileName, isNullOrEmpty } from "../utils/string";
import { getDefaultGetArticleDataOptions } from "./Defaults";
import { AppModule } from "./AppModule";
import { ArticleElement } from "./ArticleElement";
import { BoundingBoxDimensions } from "./BoundingBoxDimensions";
import { Helpers } from "./Helpers";
import { SubArticleElement } from "./SubArticleElement";
import { SubArticleHighlights } from "./SubArticleHighlight";
import { ARTICLEELEMENT_NODEDATA_BASKETID, ARTICLEELEMENT_NODEDATA_SELECTABLE } from "./Constants";
export class MainArticleElement extends ArticleElement {
    get hasPlaceholderGeometry() {
        return (this.mHasPlaceholderGeometry);
    }
    /**
     * If true the main article will not be visible. Only the sub-articles will be visible.
     */
    get hideMainArticle() {
        return (this.mHideMainArticle);
    }
    set hideMainArticle(pHide) {
        if (pHide !== this.mHideMainArticle) {
            this.mHideMainArticle = pHide;
            this.updateMainArticleVisibility();
        }
    }
    /**
     * Array of sub-article ids. If not null only the sub-articles contained in this array are visible
     * otherwise all sub-articles will be visible.
     */
    get visibleSubArticles() {
        return (this.mVisibleSubArticles);
    }
    set visibleSubArticles(pSubArticles) {
        if (pSubArticles !== this.mVisibleSubArticles) {
            this.mVisibleSubArticles = pSubArticles;
            this.updateSubArticleVisibility();
        }
    }
    get showDimensions() {
        return (this.mBoxDimensions != null);
    }
    set showDimensions(pShow) {
        if (pShow && this.mBoxDimensions == null) {
            this.mBoxDimensions = new BoundingBoxDimensions([this], this.mMngr, {
                color: this.dimensionsColor,
                scaleFactor: this.dimensionsScaleFactor
            });
        }
        else if (!pShow && this.mBoxDimensions != null) {
            this.mBoxDimensions.dispose();
            this.mBoxDimensions = null;
        }
    }
    /**
     * Custom scale factor of the dimension.
     * @defaultValue 1
     */
    get dimensionsScaleFactor() {
        return (this.mDimensionsScaleFactor);
    }
    set dimensionsScaleFactor(pFactor) {
        if (pFactor !== this.mDimensionsScaleFactor) {
            this.mDimensionsScaleFactor = pFactor;
            this.updateBoxDimensions();
        }
    }
    /**
     * Color of the dimensions as hex string.
     * @defaultValue "#595959"
     */
    get dimensionsColor() {
        return (this.mDimensionsColor);
    }
    set dimensionsColor(pColor) {
        if (pColor !== this.mDimensionsColor) {
            this.mDimensionsColor = pColor;
            this.updateBoxDimensions();
        }
    }
    constructor(pBasketId, pManager, pNode) {
        super(pBasketId, pManager, pNode);
        /**
         * Will be triggered inside updateGeometry() if the geometry was successfully updated.
         */
        this.eventGeometryChanged = new ListenableEvent(this);
        this.mArticleDataCache = new Map();
        this.mArticleDataOptionsCache = new Map();
        this.mBasketItemCache = new Map();
        this.mItemPropertiesCache = new Map();
        this.mCalculationCache = new Map();
        this.mCatalogImageIdCache = new Map();
        this.mOapInteractorsCache = new Map();
        this.mOapInteractorsCacheLock = new Lock();
        this.mSubArticleCache = null;
        this.mHasSubArticles = false;
        this.mHasSelectableSubArticles = false;
        this.mLastKnownUpdateState = "Unknown";
        this.mUpdateGeometryDepth = 0;
        this.mPendingGeometryUpdate = false;
        this.mWaitGeometryUpdatePromise = null;
        this.mWaitGeometryUpdateResolve = null;
        this.mObxUrl = null;
        this.mEnsureObxPromise = null;
        this.mEnsureIsInstancedPromise = null;
        this.mHideMainArticle = false;
        this.mVisibleSubArticles = null;
        this.mSubArticleHighlights = null;
        this.mEaiwsTransformUpToDate = false;
        this.mHasPlaceholderGeometry = false;
        this.mBoxDimensions = null;
        this.mDimensionsScaleFactor = 1.0;
        this.mDimensionsColor = "#595959";
        this.mModel.beginModification();
        this.createOrUpdateSubArticleElements();
        this.mModel.endModification();
        if (this.isInstanced())
            this.mMngr._registerInstancedArticle(this);
    }
    canSelectSubElement() {
        return (true);
    }
    dispose() {
        //dispose dimensions
        this.showDimensions = false;
        if (this.mSubArticleHighlights != null)
            this.mSubArticleHighlights.dispose();
        //dispose subarticles
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach((pSubElement) => {
                pSubElement.dispose();
            });
        }
        super.dispose();
    }
    isInstanced() {
        return (!isNullOrEmpty(this.mBasketId));
    }
    ensureIsInstanced() {
        if (this.mEnsureIsInstancedPromise != null)
            return (this.mEnsureIsInstancedPromise); //reentrant, return existing promise
        if (this.isInstanced())
            return (Promise.resolve());
        this.mEnsureIsInstancedPromise = this.ensureObx().then(() => {
            const tOptions = new basket.PasteOptions();
            tOptions.reuseItemIds = true;
            tOptions.returnOriginalItemIds = true;
            return (this.mMngr.session.basket.paste(this.mMngr.fatherItemId, null, this.mObxUrl, tOptions));
        }).then((pIds) => {
            this.recoverInstance(pIds);
            this.mEnsureIsInstancedPromise = null;
            this.mMngr.eventArticleInstanceRestored.trigger(this);
        }).catch((pError) => {
            Log.error("Failed to instantiate article", pError);
            this.mEnsureIsInstancedPromise = null;
        });
        return (this.mEnsureIsInstancedPromise);
    }
    getMainArticle() {
        return (this);
    }
    getParentArticle() {
        return (null);
    }
    isMainArticle() {
        return (true);
    }
    isSubArticle() {
        return (false);
    }
    hasSubArticles(pSelectableOnly) {
        return (pSelectableOnly ? this.mHasSelectableSubArticles : this.mHasSubArticles);
    }
    /**
     * Retrieves the basket items which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    fetchBasketItems() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            const tItemIds = new Array();
            //main article
            if (this.getCachedBasketItem() == null)
                tItemIds.push(this.mBasketId);
            //sub articles
            const tSubArticles = this.getSubArticles(false);
            for (const tSubArtile of tSubArticles) {
                if (tSubArtile.getCachedBasketItem() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            const tPromise = this.getEaiwsBasketItems(tItemIds).then((pItems) => {
                if (pItems != null) {
                    for (const tItem of pItems) {
                        this.mBasketItemCache.set(tItem.itemId, tItem);
                    }
                }
            });
            return (tPromise);
        });
        return (tPromise);
    }
    /**
     * Fills the internal cache with the given items.
     */
    fillBasketItemCache(pItems) {
        for (const tItem of pItems) {
            this.mBasketItemCache.set(tItem.itemId, tItem);
        }
    }
    /**
     * Retrieves the item properties which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    fetchItemProperties() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            const tItemIds = new Array();
            //main article
            if (this.getCachedItemProperties() == null)
                tItemIds.push(this.mBasketId);
            //sub articles
            const tSubArticles = this.getSubArticles(false);
            for (const tSubArtile of tSubArticles) {
                if (tSubArtile.getCachedItemProperties() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            const tPromise = this.getEaiwsItemProps(tItemIds).then((pItemProperties) => {
                if (pItemProperties != null) {
                    for (const tProps of pItemProperties)
                        this.mItemPropertiesCache.set(tProps.itemId, tProps);
                }
            });
            return (tPromise);
        });
        return (tPromise);
    }
    /**
     * Fills the internal cache with the given item properties.
     */
    fillItemPropertiesCache(pItemProps) {
        for (const tProps of pItemProps) {
            if (tProps.itemId != null) {
                this.mItemPropertiesCache.set(tProps.itemId, tProps);
            }
        }
    }
    /**
     * Retrieves the calculations which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    fetchCalculations() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            const tProc = this.mMngr.pricingProcedure;
            if (tProc == null)
                return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
            const tItemIds = new Array();
            //main article
            if (this.getCachedCalculation() == null)
                tItemIds.push(this.mBasketId);
            //sub articles
            const tSubArticles = this.getSubArticles(false);
            for (const tSubArtile of tSubArticles) {
                if (tSubArtile.getCachedCalculation() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            const tPromise = this.getEaiwsItemCalculation(tItemIds).then((pItemCalcs) => {
                for (const tItemCalc of pItemCalcs) {
                    const tCalcInfo = new CalculationSheetInfo(tProc, tItemCalc.sheet);
                    this.mCalculationCache.set(tItemCalc.id, tCalcInfo);
                }
            });
            return (tPromise);
        });
        return (tPromise);
    }
    /**
     * Fills the internal cache with the given calculations.
     */
    fillCalculationCache(pCalculations) {
        pCalculations.forEach((pValue, pKey) => {
            this.mCalculationCache.set(pKey, pValue);
        });
    }
    /**
     * Fills the internal cache with the given data.
     */
    fillConfigDataCache(pData) {
        pData.forEach((pValue, pKey) => {
            if (pValue.articleData != null)
                this.mArticleDataCache.set(pKey, pValue.articleData);
            if (pValue.articleDataOptions != null)
                this.mArticleDataOptionsCache.set(pKey, pValue.articleDataOptions);
            if (pValue.oapInteractors != null)
                this.mOapInteractorsCache.set(pKey, pValue.oapInteractors);
        });
    }
    /**
     * Returns a calculation for the main article including all its sub articles.
     */
    async getCompositeCalculation() {
        await this.ensureIsInstanced();
        if (this.mBasketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        const tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        if (this.mCompositeCalculation !== undefined)
            return (this.mCompositeCalculation);
        else {
            const tSheet = await this.getEaiwsCompositeCalculation(this.mBasketId);
            /* The EAIWS does not return a quantity for group calculations.
                In this case we can fallback to the quantity of the main article.
                This is correct because the quantity of the sub articles is always equal to the main article.
            */
            if (tSheet.quantity == null) {
                const tFallbackCalc = await this.getCalculation();
                tSheet.quantity = tFallbackCalc.sheet.quantity;
            }
            this.mCompositeCalculation = new CalculationSheetInfo(tProc, tSheet);
            return (this.mCompositeCalculation);
        }
    }
    /**
     * Notify this main article that there was a change.
     * @param pArticle The changed article. Can be a sub article of the main article or the main article itself.
     * @param pWholeComposite Shoud be true if the given article and also other articles have changed.
     */
    notifyArticleChanged(pArticle, pWholeComposite) {
        this._clearCachedArticleInfo(pArticle, pWholeComposite);
        this.mMngr._notifyArticleChanged(pWholeComposite ? this : pArticle, pWholeComposite);
    }
    async updateGeometry(pValidateSelection = true) {
        this.mPendingGeometryUpdate = true;
        if (this.mUpdateGeometryDepth > 0)
            return;
        try {
            this.mModel.beginModification();
            this.beginChange();
            await this.ensureIsInstanced();
            if (this.mBasketId == null)
                throw newError(ERR_INVALID_STATE, "basketId is null");
            let tNode = null;
            let tItems = null;
            const tResult = await this.mMngr._importGfj(this.mBasketId, null);
            if (tResult != null) {
                tNode = tResult.node;
                tItems = tResult.items;
                this._setGfjUrl(tResult.gfjUrl);
                this.fillBasketItemCache(tResult.items);
            }
            if (tNode == null) {
                //update placeholder
                const tSubArticleIds = new Array();
                if (tItems != null) {
                    for (const tItem of tItems) {
                        if (tItem.itemId != this.basketId)
                            tSubArticleIds.push(tItem.itemId);
                    }
                }
                this.mHasPlaceholderGeometry = true;
                tNode = await this.mMngr.placeholderCreator.create(this, tSubArticleIds);
            }
            else {
                this.mHasPlaceholderGeometry = false;
            }
            this.replaceNode(tNode, true, true);
            this.createOrUpdateSubArticleElements();
            if (pValidateSelection) {
                this.mMngr.app.model.validateSelection();
            }
            //apply visibility
            if (this.mHideMainArticle)
                this.updateMainArticleVisibility();
            if (this.mVisibleSubArticles != null)
                this.updateSubArticleVisibility();
            this.eventGeometryChanged.trigger();
        }
        finally {
            this.mPendingGeometryUpdate = false;
            this.endChange();
            this.mModel.endModification();
            //resolve the waiting promise
            if (this.mWaitGeometryUpdatePromise != null) {
                this.mWaitGeometryUpdateResolve();
                this.mWaitGeometryUpdateResolve = null;
                this.mWaitGeometryUpdatePromise = null;
            }
        }
    }
    beginUpdateGeometry() {
        this.mUpdateGeometryDepth++;
    }
    endUpdateGeometry() {
        this.mUpdateGeometryDepth--;
        if (this.mUpdateGeometryDepth <= 0) {
            this.mUpdateGeometryDepth = 0;
            if (this.mPendingGeometryUpdate)
                return (this.updateGeometry(true));
        }
        return (Promise.resolve());
    }
    /**
     * If there is a pending geometry update this function will wait until it is finished,
     * otherwise it will resovle immediately.
     */
    waitUntilGeometryUpdated() {
        if (this.mPendingGeometryUpdate) {
            if (this.mWaitGeometryUpdatePromise == null) {
                this.mWaitGeometryUpdatePromise = new Promise((pResolve) => {
                    this.mWaitGeometryUpdateResolve = pResolve;
                });
            }
            return (this.mWaitGeometryUpdatePromise);
        }
        else
            return (Promise.resolve());
    }
    clone(pNewParent) {
        const tClone = new MainArticleElement(null, this.mMngr, SceneElement.cloneNode(this.mNode, pNewParent));
        tClone.copyDataFrom(this);
        return (tClone);
    }
    serialize(pContext) {
        if (this.mNode == null)
            return (null);
        const tIOArticle = {};
        tIOArticle.classId = MainArticleElement.CLASS_ID;
        if (this.mHasPlaceholderGeometry)
            tIOArticle.placeholderGeometry = true;
        tIOArticle.node = serializeNode(this.mMngr.app.model, pContext, this.mNode, true, (pNode, pIONode) => {
            //add aticle information
            const tBasketId = NodeUserData.GetData(pNode, ARTICLEELEMENT_NODEDATA_BASKETID);
            if (!isNullOrEmpty(tBasketId)) {
                const tArticleNode = pIONode;
                //basket id
                tArticleNode.basketId = tBasketId;
                //catalog image
                const tCatImage = this._getCatalogImageId(tBasketId);
                if (tCatImage !== undefined) { //cache may contain null
                    const tCatImageIdx = AppModule._GetCatalogImageIndex(tCatImage, pContext);
                    if (tCatImageIdx != null)
                        tArticleNode.catalogImage = tCatImageIdx;
                    else
                        tArticleNode.catalogImage = null;
                }
                //selectable
                const tSelectable = NodeUserData.GetData(pNode, ARTICLEELEMENT_NODEDATA_SELECTABLE);
                if (tSelectable != null && !tSelectable)
                    tArticleNode.selectable = false;
            }
        });
        return (tIOArticle);
    }
    /**
     * Returns the currently known ofml update state.
     */
    getKnownOfmlUpdateState() {
        const tProps = this.getCachedItemProperties();
        if (tProps != null && tProps.article != null && tProps.article.ofmlUpdateState != null) {
            //do not override known states, item props do not return states like "NoCatalog" or "Invalid"
            if (this.mLastKnownUpdateState === "Unknown" || tProps.article.ofmlUpdateState === "UpToDate") {
                this.mLastKnownUpdateState = tProps.article.ofmlUpdateState;
            }
        }
        return (this.mLastKnownUpdateState);
    }
    getOfmlUpdateState() {
        const tKnownState = this.getKnownOfmlUpdateState();
        if (tKnownState !== "Unknown") {
            return (Promise.resolve(tKnownState));
        }
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (Helpers.getOfmlUpdateState(this.mMngr.session, this.mBasketId));
        }).then((pState) => {
            //update cached state
            this.mLastKnownUpdateState = pState;
            return (pState);
        });
        return (tPromise);
    }
    /**
     * Updates the ofml article and its sub articles to use the currently installed product data.
     * If an update is necessary can be checked with 'getOfmlUpdateState()'. Returns true if the
     * update was successful.
     * @pMigrate set to true if migration should be performed if necessary
     * @pUpdateGeometry set to true if geometry should also be updated
     * @pPriceDate price date to use for the update
     */
    async updateOfmlArticle(pMigrate, pUpdateGeometry, pPriceDate) {
        await this.ensureIsInstanced();
        if (this.mBasketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        const tNewState = await Helpers.updateOfmlArticle(this.mMngr.session, this.mBasketId, pMigrate, pPriceDate);
        //update cached state
        this.mLastKnownUpdateState = tNewState;
        if (tNewState === "UpToDate") {
            this._clearCachedArticleInfo(this, true);
            //trigger property change event
            this.notifyChange(ElementChangedFlags.Properties);
            const tSubArticles = this.getSubArticles(false);
            for (const tSubArticle of tSubArticles)
                tSubArticle.notifyChange(ElementChangedFlags.Properties);
            if (pUpdateGeometry) {
                await this.updateGeometry();
            }
            this.mMngr._notifyArticleChanged(this, true);
            return (true);
        }
        return (false);
    }
    /**
     * Enables highlighting of selectabe sub articles for this main article. The highlighting will be disabled
     * automatically e.g. if the article changed or a tool was started.
     * Returns false if the default tool is currently not running (if a default tool exists)
     * or the highlighting is enabled already.
     * @param pCreateCallback Called after creation of the highlighting.
     * @param pDisposeCallback Called if the highighting was diposed/disabled.
     */
    highlightSubArticles(pCreateCallback, pDisposeCallback) {
        if (this.mSubArticleHighlights != null)
            return (false);
        this.mSubArticleHighlights = SubArticleHighlights.Create(this, this.mMngr, pCreateCallback, () => {
            this.mSubArticleHighlights = null;
            if (pDisposeCallback != null)
                pDisposeCallback();
        });
        return (this.mSubArticleHighlights != null);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    async _initPlaceholderGeometry(pItems) {
        try {
            this.mModel.beginModification();
            //get sub article ids
            const tSubArticleIds = new Array();
            if (pItems != null) {
                //use given ids
                for (const tItem of pItems) {
                    if (tItem.itemId != this.basketId)
                        tSubArticleIds.push(tItem.itemId);
                }
            }
            else {
                //use current sub nodes
                if (this.mNode != null) {
                    const tChildNodes = this.mNode.getChildTransformNodes(false);
                    for (const tChild of tChildNodes) {
                        const tSubId = NodeUserData.GetData(tChild, ARTICLEELEMENT_NODEDATA_BASKETID);
                        if (tSubId != null)
                            tSubArticleIds.push(tSubId);
                    }
                }
            }
            const tMesh = await this.mMngr.placeholderCreator.create(this, tSubArticleIds);
            if (this.mNode == null)
                this.setNode(tMesh);
            else
                this.replaceNode(tMesh, true, true);
            this.mHasPlaceholderGeometry = true;
            this.createOrUpdateSubArticleElements();
            this.mModel.endModification();
        }
        finally {
            this.mModel.endModification();
        }
    }
    /** @internal */
    _clearCachedArticleInfo(pArticle, pWholeComposite) {
        if (pWholeComposite) {
            //clear whole cache
            this.mArticleDataCache.clear();
            this.mArticleDataOptionsCache.clear();
            this.mBasketItemCache.clear();
            this.mItemPropertiesCache.clear();
            this.mCalculationCache.clear();
            this.mCatalogImageIdCache.clear();
            this.mOapInteractorsCache.clear();
        }
        else {
            if (pArticle != null && pArticle.basketId != null) {
                this.mArticleDataCache.delete(pArticle.basketId);
                this.mArticleDataOptionsCache.delete(pArticle.basketId);
                this.mBasketItemCache.delete(pArticle.basketId);
                this.mItemPropertiesCache.delete(pArticle.basketId);
                this.mCalculationCache.delete(pArticle.basketId);
                this.mCatalogImageIdCache.delete(pArticle.basketId);
                this.mOapInteractorsCache.delete(pArticle.basketId);
            }
        }
        this.mCompositeCalculation = undefined;
        //reset cached obx
        this.mObxUrl = null;
        //article changed, disable highlights
        if (this.mSubArticleHighlights != null) {
            this.mSubArticleHighlights.dispose();
        }
    }
    /** @internal */
    _getArticleData(pBasketId, pOptions) {
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        const tRequestedOptions = pOptions !== null && pOptions !== void 0 ? pOptions : getDefaultGetArticleDataOptions();
        tRequestedOptions.separateCurrencies = true; //force to be true
        //check cache first
        const tArticleData = this._getCachedArticleData(pBasketId, tRequestedOptions);
        if (tArticleData != null)
            return (Promise.resolve(tArticleData));
        let tPromise;
        if (this.mMngr.session.isValid) {
            tPromise = this.mMngr.session.basket.getArticleData(pBasketId, tRequestedOptions);
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        tPromise = tPromise.then((pArticleData) => {
            const tCachedOptions = this.mArticleDataOptionsCache.get(pBasketId);
            const tCachedData = this.mArticleDataCache.get(pBasketId);
            if (tCachedOptions == null || tCachedData == null) {
                //nothing cached yet, add entry
                const tOptions = new basket.GetArticleDataOptions();
                Object.assign(tOptions, tRequestedOptions); //create copy
                this.mArticleDataCache.set(pBasketId, pArticleData);
                this.mArticleDataOptionsCache.set(pBasketId, tOptions);
            }
            else {
                //update cached articledata
                if (pArticleData != null) {
                    //properties
                    if (!tRequestedOptions.noProperties) {
                        tCachedOptions.noProperties = false;
                        tCachedData.properties = pArticleData.properties;
                        tCachedData.propertyClasses = pArticleData.propertyClasses;
                    }
                    //catalog icon
                    if (tRequestedOptions.fetchCatalogIcon) {
                        tCachedOptions.fetchCatalogIcon = true;
                        tCachedData.catalogIcon = pArticleData.catalogIcon;
                    }
                    //catalog image
                    if (tRequestedOptions.fetchCatalogImage) {
                        tCachedOptions.fetchCatalogImage = true;
                        tCachedData.catalogImage = pArticleData.catalogImage;
                    }
                }
            }
            return (pArticleData);
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedArticleData(pBasketId, pOptions) {
        let tRequestedOptions = pOptions;
        if (tRequestedOptions == null)
            tRequestedOptions = getDefaultGetArticleDataOptions();
        //compare cached with requested options
        const tCachedOptions = this.mArticleDataOptionsCache.get(pBasketId);
        if (tCachedOptions == null)
            return (undefined); //nothing cached
        //properties not cached
        if (tCachedOptions.noProperties && !tRequestedOptions.noProperties)
            return (undefined);
        //icon not cached
        if (!tCachedOptions.fetchCatalogIcon && tRequestedOptions.fetchCatalogIcon)
            return (undefined);
        //image not cached
        if (!tCachedOptions.fetchCatalogImage && tRequestedOptions.fetchCatalogImage)
            return (undefined);
        return (this.mArticleDataCache.get(pBasketId));
    }
    /** @internal */
    _getBasketItem(pBasketId) {
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        const tItem = this.mBasketItemCache.get(pBasketId);
        if (tItem != null)
            return (Promise.resolve(tItem));
        const tPromise = this.getEaiwsBasketItems([pBasketId]).then((pItems) => {
            if (pItems.length !== 0) {
                this.mBasketItemCache.set(pBasketId, pItems[0]);
                return (Promise.resolve(pItems[0]));
            }
            else {
                return (Promise.reject(new Error("Failed to get item " + pBasketId)));
            }
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedBasketItem(pBasketId) {
        return (this.mBasketItemCache.get(pBasketId));
    }
    /** @internal */
    _getItemProperties(pBasketId) {
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        const tItemProperties = this.mItemPropertiesCache.get(pBasketId);
        if (tItemProperties != null)
            return (Promise.resolve(tItemProperties));
        const tPromise = this.getEaiwsItemProps([pBasketId]).then((pItemProperties) => {
            if (pItemProperties.length !== 0) {
                this.mItemPropertiesCache.set(pBasketId, pItemProperties[0]);
                return (Promise.resolve(pItemProperties[0]));
            }
            else {
                return (Promise.reject(new Error("Failed to get item properties for item " + pBasketId)));
            }
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedItemProperties(pBasketId) {
        return (this.mItemPropertiesCache.get(pBasketId));
    }
    /** @internal */
    _getCalculation(pBasketId) {
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        const tCalculation = this.mCalculationCache.get(pBasketId);
        if (tCalculation != null)
            return (Promise.resolve(tCalculation));
        const tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        const tPromise = this.getEaiwsItemCalculation([pBasketId]).then((pItemCalcs) => {
            if (pItemCalcs.length !== 0) {
                const tCalcInfo = new CalculationSheetInfo(tProc, pItemCalcs[0].sheet);
                this.mCalculationCache.set(pBasketId, tCalcInfo);
                return (Promise.resolve(tCalcInfo));
            }
            else {
                return (Promise.reject(new Error("Failed to get calculation for item " + pBasketId)));
            }
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedCalculation(pBasketId) {
        return (this.mCalculationCache.get(pBasketId));
    }
    /** @internal **/
    async _getCatalogImage(pBasketId) {
        if (pBasketId == null)
            return (null);
        //check cache first
        let tImageId = this.mCatalogImageIdCache.get(pBasketId);
        if (tImageId !== undefined) //cache may contain null
            return (Promise.resolve(this.mMngr._getCatalogImageFromCache(tImageId)));
        //get article data
        const tOptions = new basket.GetArticleDataOptions();
        tOptions.noProperties = true;
        tOptions.fetchCatalogIcon = true;
        tOptions.fetchCatalogImage = true;
        const tArticleData = await this._getArticleData(pBasketId, tOptions);
        let tCatalogImage = undefined;
        tCatalogImage = tArticleData.catalogImage;
        if (isNullOrEmpty(tCatalogImage))
            tCatalogImage = tArticleData.catalogIcon;
        if (isNullOrEmpty(tCatalogImage)) {
            this.mCatalogImageIdCache.set(pBasketId, null);
            return (null);
        }
        else {
            const tBuffer = await ajax("GET", tCatalogImage, null, { responseType: "arraybuffer" });
            tImageId = await hash(tBuffer, "SHA-256");
            tImageId = tImageId.substr(0, 32); // reduce to 128 bit, should be enough in this case
            const tImage = {};
            tImage.data = tBuffer;
            tImage.name = getFileName(tCatalogImage);
            this.mMngr._addCatalogImageToCache(tImageId, tImage);
            this.mCatalogImageIdCache.set(pBasketId, tImageId);
            return (tImage);
        }
    }
    /** @internal **/
    _getCatalogImageId(pBasketId) {
        if (pBasketId == null)
            return (null);
        const tId = this.mCatalogImageIdCache.get(pBasketId);
        return (tId != null ? tId : null);
    }
    /** @internal */
    _getCachedConfigData(pBasketId) {
        const tData = {};
        tData.articleData = this.mArticleDataCache.get(pBasketId);
        tData.articleDataOptions = this.mArticleDataOptionsCache.get(pBasketId);
        tData.oapInteractors = this.mOapInteractorsCache.get(pBasketId);
        tData.gfjUrl = this.basketId === pBasketId ? this.mGfjUrl : undefined;
        return (tData);
    }
    /** @internal */
    _resetInstance() {
        this.mBasketId = null;
    }
    /** @internal */
    _prepareInstanceRecoveryData() {
        return (this.ensureObx());
    }
    /** @internal */
    _saveTransformToEaiws() {
        if (this.mEaiwsTransformUpToDate)
            return (Promise.resolve());
        if (!this.isInstanced() || this.mNode == null || this.mBasketId == null)
            return (rejectError(ERR_INVALID_STATE));
        this.mEaiwsTransformUpToDate = true;
        const tProps = new basket.ItemProperties();
        tProps.composableGeometry = new basket.ComposableGeometryProperties();
        const tGeomProps = tProps.composableGeometry;
        tGeomProps.enabled = true;
        //position
        tGeomProps.position = new basket.Vector3();
        tGeomProps.position.x = this.mNode.position.x;
        tGeomProps.position.y = this.mNode.position.y;
        tGeomProps.position.z = -this.mNode.position.z;
        //rotation
        const tAxisAngle = quaternionToAxisAngle(getRotationQuaternion(this.mNode));
        tGeomProps.rotation = new basket.Rotation();
        tGeomProps.rotation.axis = new basket.Vector3();
        tGeomProps.rotation.axis.x = tAxisAngle.x;
        tGeomProps.rotation.axis.y = tAxisAngle.y;
        tGeomProps.rotation.axis.z = -tAxisAngle.z;
        tGeomProps.rotation.angle = -tAxisAngle.w;
        //scale
        if (this.supportScaleEaiwsTransform()) {
            tGeomProps.scale = new basket.Vector3();
            tGeomProps.scale.x = this.mNode.scaling.x;
            tGeomProps.scale.y = this.mNode.scaling.y;
            tGeomProps.scale.z = this.mNode.scaling.z;
        }
        const tPromise = this.mMngr.session.basket.setItemProperties(this.mBasketId, tProps).catch((pError) => {
            Log.error("Failed to save transformation", pError);
        });
        return (tPromise);
    }
    /** @internal */
    _loadEaiwsTransform(pGeomProps) {
        if (pGeomProps == null || !pGeomProps.enabled)
            return;
        //position
        Helpers.Vector3CopyFromEaiwsVector(this.mNode.position, pGeomProps.position);
        //rotation
        this.mNode.rotationQuaternion = Helpers.QuaternionFromEaiwsRotation(pGeomProps.rotation);
        //scale
        if (this.supportScaleEaiwsTransform() && pGeomProps.scale != null) {
            this.mNode.scaling.x = pGeomProps.scale.x;
            this.mNode.scaling.y = pGeomProps.scale.y;
            this.mNode.scaling.z = pGeomProps.scale.z;
        }
        else {
            this.mNode.scaling.set(1, 1, 1);
        }
        this.mEaiwsTransformUpToDate = true;
        this.updateTransformation();
    }
    /** @internal */
    async _getOapInteractors(pArticle) {
        if (pArticle == null)
            return (null);
        if (!this.mMngr.session.isValid)
            return (null);
        await pArticle.ensureIsInstanced();
        if (pArticle.basketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        const tMainArticle = pArticle.getMainArticle();
        //do not show interactors if article has no geometry
        if (tMainArticle.hasPlaceholderGeometry || !tMainArticle.hasGeometry) {
            return (null);
        }
        const tLockContext = await this.mOapInteractorsCacheLock.acquireLock();
        try {
            //check cache first
            let tInteractors = this.mOapInteractorsCache.get(pArticle.basketId);
            if (tInteractors != null)
                return (tInteractors);
            const tDpr = window.devicePixelRatio > 1.5 ? 2 : 1; //define device pixel ratio
            tInteractors = await this.mMngr.session.basket.oapGetInteractors(pArticle.basketId, undefined, tDpr);
            this.mOapInteractorsCache.set(pArticle.basketId, tInteractors);
            return (tInteractors);
        }
        finally {
            tLockContext.unlock();
        }
    }
    /** @internal */
    static _LoadArticleElement(pApp, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        const tIOArticle = pIOElement;
        let tIsPlaceholder = tIOArticle.placeholderGeometry != null ?
            tIOArticle.placeholderGeometry : false;
        const tModule = pApp.getModule(AppModule.MODULE_KEY);
        const tLoadedNode = MainArticleElement.LoadArticleElementNode(pApp, pContext, pIOElement);
        if (tLoadedNode == null || tLoadedNode.basketId == null || tModule == null)
            return (null);
        const tMainArticle = new MainArticleElement(tLoadedNode.basketId, tModule.articleManager, tLoadedNode.node);
        tMainArticle.setCatalogImageIdCache(tLoadedNode.catalogImageIds);
        if (tMainArticle.mNode == null)
            tIsPlaceholder = true;
        //register update geometry task
        if (tLoadedNode.geometryIncomplete && tModule.articleManager.session.isValid) {
            pContext.registerPendingTask(() => tMainArticle.updateGeometry(false));
        }
        else if (tIsPlaceholder) {
            pContext.registerPendingTask(() => tMainArticle._initPlaceholderGeometry(null));
        }
        return (tMainArticle);
    }
    /** @internal */
    _setGfjUrl(pUrl) {
        this.mGfjUrl = pUrl;
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    handleImmediateChanges() {
        super.handleImmediateChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Transformation)) {
            this.mEaiwsTransformUpToDate = false;
        }
        if (this.wasFlagChanged(ElementChangedFlags.Removed)) {
            //dispose dimensions
            this.showDimensions = false;
        }
    }
    copyDataFrom(pElement) {
        super.copyDataFrom(pElement);
        if (pElement == null || !(pElement instanceof MainArticleElement))
            return;
        //copy catalog image cache (with basket ids from other element)
        this.mCatalogImageIdCache = new Map(pElement.mCatalogImageIdCache);
        this.mHasPlaceholderGeometry = pElement.mHasPlaceholderGeometry;
        //set correct main article for cloned sub elements
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach((pSubElement) => {
                pSubElement._setMainArticle(this);
            });
        }
        //copy obx
        this.mEnsureObxPromise = pElement.ensureObx().then(() => {
            this.mObxUrl = pElement.mObxUrl;
            this.mEnsureObxPromise = null;
        });
    }
    setCatalogImageIdCache(pCache) {
        if (pCache != null)
            this.mCatalogImageIdCache = pCache;
    }
    static LoadArticleElementNode(pApp, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        const tResult = {
            node: null,
            basketId: null,
            geometryIncomplete: false,
            catalogImageIds: new Map()
        };
        const tIOArticle = pIOElement;
        tResult.node = deserializeNode(pApp.model, pContext, tIOArticle.node, null, (pIONode, pNode) => {
            const tIONode = pIONode;
            //get article info
            if (tIONode.basketId != null) {
                //basket id
                NodeUserData.SetData(pNode, ARTICLEELEMENT_NODEDATA_BASKETID, tIONode.basketId);
                //catalog image
                if (tIONode.catalogImage !== undefined) { //may be null
                    const tImgId = AppModule._GetCatalogImageByIndex(tIONode.catalogImage, pContext);
                    tResult.catalogImageIds.set(tIONode.basketId, tImgId);
                }
                //selectable
                let tSelectable = true;
                if (tIONode.selectable != null)
                    tSelectable = tIONode.selectable;
                NodeUserData.SetData(pNode, ARTICLEELEMENT_NODEDATA_SELECTABLE, tSelectable);
            }
            if (pNode instanceof Mesh) {
                //is geometry missing?
                if (tIONode.geometry != null) {
                    if (pNode.geometry == null)
                        tResult.geometryIncomplete = true;
                    else if (pApp.model.geometries.isFallbackGeometry(pNode.geometry))
                        tResult.geometryIncomplete = true;
                }
                //is texture missing?
                if (tIONode.material != null) {
                    if (pNode.material == null)
                        tResult.geometryIncomplete = true;
                    else if (pNode.material instanceof PbrMaterial && pNode.material.hasFallbackTextures())
                        tResult.geometryIncomplete = true;
                }
            }
        });
        tResult.basketId = NodeUserData.GetData(tResult.node, ARTICLEELEMENT_NODEDATA_BASKETID);
        if (tResult.node == null || tResult.basketId == null)
            return (null);
        return (tResult);
    }
    supportScaleEaiwsTransform() {
        return (false);
    }
    recoverInstance(pPasteObxIds) {
        if (this.mNode == null || pPasteObxIds == null || pPasteObxIds.length % 2 !== 0)
            return (false);
        //build map from old to new id
        const tIdMap = new Map();
        for (let i = 0; i < pPasteObxIds.length; i += 2)
            tIdMap.set(pPasteObxIds[i + 1], pPasteObxIds[i]);
        //fix ids in catalog image cache (copied in copyDataFrom)
        tIdMap.forEach((pNewId, pOldId) => {
            const tImage = this.mCatalogImageIdCache.get(pOldId);
            if (tImage !== undefined) //may be null
                this.mCatalogImageIdCache.set(pNewId, tImage);
        });
        //recover subarticles
        if (this.mSubArticleCache == null)
            this.mSubArticleCache = new Map();
        else
            this.mSubArticleCache.clear();
        const tSubArticles = this.getSubArticles(false);
        for (const tSubArticle of tSubArticles) {
            const tNewId = tIdMap.get(tSubArticle._getRecoveryBasketId());
            if (tNewId != null) {
                tSubArticle._setBasketId(tNewId);
                this.mSubArticleCache.set(tNewId, tSubArticle);
            }
        }
        //recover main article
        const tNewId = tIdMap.get(this.mRecoveryBasketId);
        if (tNewId != null) {
            //reset cached obx to ensure it contains the the correct basket ids the next time it is used
            this.mObxUrl = null;
            this._setBasketId(tNewId);
            this.mMngr._registerInstancedArticle(this);
            return (true);
        }
        return (false);
    }
    createOrUpdateSubArticleElements() {
        this.mHasSubArticles = false;
        this.mHasSelectableSubArticles = false;
        const tCachedSubArticles = new Map();
        this.createSubArticleElements(this.mNode, tCachedSubArticles);
        //trigger removed event for removed sub articles and dispose them
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach((pElement, pId) => {
                if (!tCachedSubArticles.has(pId)) {
                    pElement.notifyChange(ElementChangedFlags.Removed);
                    pElement.dispose();
                }
            });
        }
        this.mSubArticleCache = tCachedSubArticles;
    }
    createSubArticleElements(pParent, pNewCache) {
        if (pParent == null)
            return;
        const tChilds = pParent.getChildTransformNodes(true);
        for (const tChild of tChilds) {
            const tBasketId = NodeUserData.GetData(tChild, ARTICLEELEMENT_NODEDATA_BASKETID);
            if (tBasketId != null) {
                let tSubArticle = null;
                if (this.mSubArticleCache != null) //get from cache
                    tSubArticle = this.mSubArticleCache.get(tBasketId);
                if (tSubArticle != null) {
                    //store new selectable flag as it will be overriden from replaceNode
                    const tSelectable = NodeUserData.GetData(tChild, ARTICLEELEMENT_NODEDATA_SELECTABLE);
                    tSubArticle.replaceNode(tChild, false, false, false);
                    //update selectable flag as it may have changed
                    NodeUserData.SetData(tSubArticle.node, ARTICLEELEMENT_NODEDATA_SELECTABLE, tSelectable);
                }
                else {
                    tSubArticle = new SubArticleElement(this, tBasketId, this.mMngr, tChild);
                    //trigger added event
                    tSubArticle.notifyChange(ElementChangedFlags.Added);
                }
                this.mHasSubArticles = true;
                this.mHasSelectableSubArticles = this.mHasSelectableSubArticles || tSubArticle.canSelect();
                pNewCache.set(tBasketId, tSubArticle);
            }
            this.createSubArticleElements(tChild, pNewCache);
        }
    }
    ensureObx() {
        if (this.mEnsureObxPromise != null)
            return (this.mEnsureObxPromise); //reentrant, return existing promise
        if (!isNullOrEmpty(this.mObxUrl))
            return (Promise.resolve());
        if (this.mBasketId == null)
            return (rejectError(ERR_INVALID_STATE, "basketId is null"));
        this.mEnsureObxPromise = this.mMngr.session.basket.copy([this.mBasketId]).then((pUrl) => {
            this.mObxUrl = pUrl;
            this.mEnsureObxPromise = null;
            return (Promise.resolve());
        }).catch((pError) => {
            Log.error("Failed to get obx", pError);
            this.mEnsureObxPromise = null;
        });
        return (this.mEnsureObxPromise);
    }
    getEaiwsBasketItems(pItemIds) {
        let tPromise;
        if (this.mMngr.session.isValid) {
            const tOptions = new basket.GetAllItemsOptions();
            tOptions.geometryIds = true;
            tOptions.setArticleIds = true;
            tPromise = this.mMngr.session.basket.getAllItems(pItemIds, tOptions);
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        return (tPromise);
    }
    getEaiwsItemProps(pItemIds) {
        let tPromise;
        if (this.mMngr.session.isValid) {
            tPromise = this.mMngr.session.basket.getItemProperties(pItemIds, this.mMngr._GetDefaultItemPropsOptions());
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        return (tPromise);
    }
    getEaiwsItemCalculation(pItemIds) {
        const tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        let tPromise;
        if (this.mMngr.session.isValid)
            tPromise = this.mMngr.session.basket.getPriceCalculationSheets(pItemIds, tProc.name);
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session not valid");
        }
        return (tPromise);
    }
    getEaiwsCompositeCalculation(pItemId) {
        const tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        const tOptions = new basket.GetPriceCalculationSheetOptions();
        tOptions.wholeComposite = true;
        //ensure correct price for alternative articles
        tOptions.excludeInactiveItems = false;
        tOptions.includeInactiveItems = true;
        let tPromise;
        if (this.mMngr.session.isValid)
            tPromise = this.mMngr.session.basket.getPriceCalculationSheet([pItemId], tProc.name, tOptions);
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session not valid");
        }
        return (tPromise);
    }
    updateMainArticleVisibility() {
        const tNodes = this.getNodes(false);
        for (const tNode of tNodes) {
            if (tNode instanceof AbstractMesh)
                tNode.isVisible = !this.mHideMainArticle;
        }
    }
    updateSubArticleVisibility() {
        const tSubArticles = this.getSubArticles(false);
        for (const tArticle of tSubArticles) {
            const tIsVisible = this.mVisibleSubArticles == null ||
                contains(this.mVisibleSubArticles, tArticle.basketId);
            const tNodes = tArticle.getNodes(false);
            for (const tNode of tNodes) {
                if (tNode instanceof AbstractMesh)
                    tNode.isVisible = tIsVisible;
            }
        }
    }
    updateBoxDimensions() {
        if (this.showDimensions) {
            this.showDimensions = false;
            this.showDimensions = true;
        }
    }
}
MainArticleElement.CLASS_ID = "egr.wcf.cf:Article";
